import { useForm } from "react-hook-form";
import Container from "../components/Container";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import Button from "../components/form/button";
import { Status } from "../lib/types";
import { useState } from "react";
import ErrorMessage from "../components/form/errorMessage";
import pb from "../lib/pocketbase";
import { useToast } from "../components/ui/use-toast";
import { ClientResponseError } from "pocketbase";
import { Socials } from "../components/Footer";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

interface InboxData {
    subject: string;
    email: string;
    message: string;
}
export default function Contact() {
    const { register, handleSubmit, reset } = useForm<InboxData>();
    const { toast } = useToast();
    const [status, setStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/;
    function resetStatus() {
        setTimeout(() => {
            setStatus("none");
        }, 1500);
    }
    function sendMessage(data: InboxData) {
        setStatus("loading");
        if (data.subject.length < 3) {
            setError("Subject must at least be 3 characters.");
            setStatus("error");
            resetStatus();
            return;
        }
        if (!data.email || !emailRegex.test(data.email)) {
            setError("Your e-mailadres is not set or invalid.");
            setStatus("error");
            resetStatus();
            return;
        }
        if (data.message.length < 3) {
            setError("Your message must be at least 3 characters.");
            setStatus("error");
            resetStatus();
            return;
        }
        data.message = data.message.replace(/\n/g, "<br />");
        pb.collection("inbox")
            .create(data)
            .then(() => {
                reset();
                setStatus("success");
                toast({
                    title: "🎉 Message sent!",
                    description: "I've received your message and will contact u soon!"
                });
            })
            .catch((err: ClientResponseError) => {
                setStatus("error");
                setError(err?.response?.message);
            })
            .finally(() => {
                resetStatus();
            });
    }
    return (
        <Container className="mt-4 lg:mt-8 xl:mt-16">
            <Helmet>
                <title>ReinVB — Contact</title>
                <meta name="description" content="Do you want to come in contact with Rein Vanbelleghem?" />
            </Helmet>
            <h1 className="mb-2 text-4xl font-medium">Contact me.</h1>
            <motion.hr animate={{ width: "100%" }} transition={{ duration: 2 }} className={`mb-16 w-0 max-w-[12rem] rounded border-t-4 border-blue-400 dark:border-blue-400`} />
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-16">
                <form className="flex flex-col gap-6 lg:col-span-3" onSubmit={handleSubmit(sendMessage)}>
                    <ErrorMessage error={error} close={() => setError("")} />
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Message subject</h3>
                        <Input type="text" {...register("subject")} placeholder="Subject of your message" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Your e-mailadres</h3>
                        <Input type="text" {...register("email")} placeholder="john@example.com" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold">Your message</h3>
                        <Textarea {...register("message")} placeholder="Hey, what is going on?" rows={8} />
                    </div>
                    <Button status={status} variant="dark" type="submit" className="w-fit">
                        {status === "loading" && "Sending"}
                        {status === "success" && "Sended"}
                        {status === "error" && "Error"}
                        {status === "none" && "Send"}
                    </Button>
                </form>
                <div className="lg:col-span-2">
                    <h3 className="mb-4 text-2xl font-medium">Let's Connect</h3>
                    <p>I appreciate you checking out my portfolio and getting to know me better. Whether you're interested in working together, have questions, or simply want to say hi, I'm here and eager to connect. Reach out using the info below or the form – I'll be quick to respond.</p>
                    <Socials className="mt-8 justify-start text-slate-400 dark:text-slate-600" />
                </div>
            </div>
        </Container>
    );
}
